import { useEffect, useState } from "react";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useMember } from "@/contexts/memberContext";
import { getInitials, getLocationLabel, isCurrentSlug } from "@/lib/utils";
import { LocationService } from "@/services/LocationService";
import type { Location } from "@/types/Location";
import {
  Armchair,
  BellDot,
  BriefcaseBusiness,
  CalendarDays,
  ChartSpline,
  ChevronDown,
  ChevronRight,
  HousePlug,
  Settings2,
  Stamp,
  Users,
} from "lucide-react";

type PracticeManagerSecondaryNavProps = {
  handleClick?: (url: string) => void;
  isExpanded: boolean;
  onToggle: () => void;
};

const PracticeManagerSecondaryNav = ({ handleClick, isExpanded, onToggle }: PracticeManagerSecondaryNavProps) => {
  const { open } = useSidebar();
  const { member } = useMember();

  const [fetchedLocations, setFetchedLocations] = useState<Location[]>([]);

  // If practiceManagerLocations is already hydrated, use it; otherwise fall back to fetched locations
  const locations = (member?.practiceManagerLocations && member.practiceManagerLocations.length > 0)
    ? member.practiceManagerLocations
    : fetchedLocations;

  // Fetch locations from API if not hydrated but IDs exist
  useEffect(() => {
    if (
      member?.practiceManagerLocationIds &&
      member.practiceManagerLocationIds.length > 0 &&
      (!member.practiceManagerLocations || member.practiceManagerLocations.length === 0)
    ) {
      const locationService = new LocationService();
      Promise.all(
        member.practiceManagerLocationIds.map((id) => locationService.getLocationById(id)),
      ).then((results) => {
        setFetchedLocations(results.filter((loc): loc is Location => loc !== null));
      });
    }
  }, [member?.practiceManagerLocationIds, member?.practiceManagerLocations]);

  // Track which practice manager locations are expanded
  const [expandedLocations, setExpandedLocations] = useState<Set<string>>(() => {
    // Expand first location by default
    if (locations.length > 0) {
      return new Set([locations[0].id]);
    }
    return new Set();
  });

  const toggleLocationExpanded = (locationId: string) => {
    setExpandedLocations((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(locationId)) {
        newSet.delete(locationId);
      } else {
        newSet.add(locationId);
      }
      return newSet;
    });
  };

  // Early return if no locations - prevents rendering empty Practice Management section
  const hasLocationIds = member?.practiceManagerLocationIds && member.practiceManagerLocationIds.length > 0;
  if (!member?.role?.includes("practice_manager") || (!hasLocationIds && locations.length === 0)) {
    return null;
  }

  return (
    <SidebarGroup className="-ml-2.5 pr-0">
      {/* Practice Management header - collapsible */}
      {open && (
        <SidebarHeader
          className="text-sm flex justify-between flex-row bg-emerald-300 ml-2 mr-0 px-1 pl-2 rounded-lg hover:bg-emerald-400 cursor-pointer mb-1"
          onClick={onToggle}
        >
          <div className="flex items-center gap-2">
            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <span>Practice Management</span>
          </div>
        </SidebarHeader>
      )}

      {isExpanded && (
        <div className="bg-emerald-200 px-1 ml-2 mr-0 rounded-lg">
          {/* Global PM items — not per-location */}
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  tooltip="Organisations"
                  data-testid="pm-nav-organisations"
                  className={`hover:cursor-pointer hover:bg-emerald-600 hover:text-white ${
                    isCurrentSlug("/practice/organisations") ? "bg-emerald-800 text-white" : ""
                  }`}
                  onClick={() => handleClick && handleClick("/practice/organisations")}
                >
                  <BriefcaseBusiness />
                  <span>Organisations</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
          <SidebarGroup>
            <SidebarGroupContent>
              {locations.map((location) => {
                  const isLocationExpanded = expandedLocations.has(location.id);
                  return (
                    <div key={location.id}>
                      {/* Location header - collapsible */}
                      {open && (
                        <SidebarHeader
                          className="font-semibold flex justify-between flex-row w-full bg-emerald-100 px-1 pl-2 rounded-lg hover:bg-emerald-200 cursor-pointer mb-1"
                          onClick={() => toggleLocationExpanded(location.id)}
                        >
                          <div className="flex items-center gap-2">
                            {isLocationExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            <span className="text-sm">
                              {getLocationLabel(location)}
                            </span>
                          </div>
                        </SidebarHeader>
                      )}

                      {!open && (
                        <SidebarHeader className="p-0 text-center flex items-center justify-center w-full">
                          <TooltipProvider delayDuration={0}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span
                                  onClick={() => toggleLocationExpanded(location.id)}
                                  className="px-1 py-1 hover:bg-emerald-900 hover:text-white hover:cursor-pointer hover:rounded-full"
                                >
                                  {getInitials(location.name)}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent align="center" side="right">
                                <div>
                                  {getLocationLabel(location)}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </SidebarHeader>
                      )}

                      {/* Location menu items */}
                      {isLocationExpanded && (
                        <SidebarMenu className={`${!open ? "-ml-2" : ""}`}>
                          <SidebarMenuItem>
                            <SidebarMenuButton
                              tooltip={`View Calendar for ${getLocationLabel(location)}`}
                              className={`hover:cursor-pointer hover:bg-emerald-600 hover:text-white ${
                                isCurrentSlug(`/location/${location.id}/calendar`) ? "bg-emerald-800 text-white" : ""
                              }`}
                              onClick={() => handleClick && handleClick(`/location/${location.id}/calendar`)}
                            >
                              <CalendarDays />
                              <span>Calendar</span>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                          <SidebarMenuItem>
                            <SidebarMenuButton
                              tooltip={`View Calendar for ${getLocationLabel(location)}`}
                              className={`hover:cursor-pointer hover:bg-emerald-600 hover:text-white ${
                                isCurrentSlug(`/location/${location.id}/practice-bookings`)
                                  ? "bg-emerald-800 text-white"
                                  : ""
                              }`}
                              onClick={() => handleClick && handleClick(`/location/${location.id}/practice-bookings`)}
                            >
                              <Stamp />
                              <span>Bookings</span>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                          <SidebarMenuItem>
                            <SidebarMenuButton
                              tooltip={`Manage Members of ${getLocationLabel(location)}`}
                              className={`hover:cursor-pointer hover:bg-emerald-600 hover:text-white ${
                                isCurrentSlug(`/location/${location.id}/practice-members`)
                                  ? "bg-emerald-800 text-white"
                                  : ""
                              }`}
                              onClick={() => handleClick && handleClick(`/location/${location.id}/practice-members`)}
                            >
                              <Users />
                              <span>Members</span>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                          <SidebarMenuItem>
                            <SidebarMenuButton
                              tooltip={`Manage Rooms in ${getLocationLabel(location)}`}
                              className={`hover:cursor-pointer hover:bg-emerald-600 hover:text-white ${
                                isCurrentSlug(`/location/${location.id}/practice-rooms`)
                                  ? "bg-emerald-800 text-white"
                                  : ""
                              }`}
                              onClick={() => handleClick && handleClick(`/location/${location.id}/practice-rooms`)}
                            >
                              <Armchair />
                              <span>Rooms</span>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                          <SidebarMenuItem>
                            <SidebarMenuButton
                              tooltip={`Manage Amenities for ${getLocationLabel(location)}`}
                              className={`hover:cursor-pointer hover:bg-emerald-600 hover:text-white ${
                                isCurrentSlug(`/location/${location.id}/practice-amenities`)
                                  ? "bg-emerald-800 text-white"
                                  : ""
                              }`}
                              onClick={() => handleClick && handleClick(`/location/${location.id}/practice-amenities`)}
                            >
                              <HousePlug />
                              <span>Amenities</span>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                          <SidebarMenuItem>
                            <SidebarMenuButton
                              tooltip={`Manage Practice Notices for ${getLocationLabel(location)}`}
                              className={`hover:cursor-pointer hover:bg-emerald-600 hover:text-white ${
                                isCurrentSlug(`/location/${location.id}/practice-notices`)
                                  ? "bg-emerald-800 text-white"
                                  : ""
                              }`}
                              onClick={() => handleClick && handleClick(`/location/${location.id}/practice-notices`)}
                            >
                              <BellDot />
                              <span>Notices</span>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                          <SidebarMenuItem>
                            <SidebarMenuButton
                              tooltip={`Reports for ${getLocationLabel(location)}`}
                              className={`hover:cursor-pointer hover:bg-emerald-600 hover:text-white ${
                                isCurrentSlug(`/location/${location.id}/practice-reports`)
                                  ? "bg-emerald-800 text-white"
                                  : ""
                              }`}
                              onClick={() => handleClick && handleClick(`/location/${location.id}/practice-reports`)}
                            >
                              <ChartSpline />
                              <span>Reports</span>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                          <SidebarMenuItem>
                            <SidebarMenuButton
                              tooltip={`My Location — ${getLocationLabel(location)}`}
                              className={`hover:cursor-pointer hover:bg-emerald-600 hover:text-white ${
                                isCurrentSlug(`/location/${location.id}/practice-settings`)
                                  ? "bg-emerald-800 text-white"
                                  : ""
                              }`}
                              onClick={() => handleClick && handleClick(`/location/${location.id}/practice-settings`)}
                            >
                              <Settings2 />
                              <span>My Location</span>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        </SidebarMenu>
                      )}
                    </div>
                  );
                })}
            </SidebarGroupContent>
          </SidebarGroup>
        </div>
      )}
    </SidebarGroup>
  );
};

export default PracticeManagerSecondaryNav;
